import { isEmpty } from "es-toolkit/compat";
import { atom } from "jotai";

import { Logger } from "@moment/logging";

import { getCache } from "../_interop/cache";
import { $accessTokenFunc } from "../authenication";
import { $docsApi } from "../docsapi";
import {
	$document,
	$removeDocumentFromDocsList,
	$restoreDocumentInDocsList,
} from "../document-list";
import { env } from "../env";
import { $deleteDocumentEnvironments } from "../environments";
import { fs } from "../filesystem";
import { $gitRemotes } from "../git";
import { $deleteDocumentWorkflows } from "../workflows";
import {
	buildWorkspaceDeleteTargets,
	deleteRemoteDocumentFromTargets,
} from "./delete-document.remote-utils";
import { GIT_PUBLISHING_REMOTE_NAME } from "./sync";

const logger = new Logger("deleteDocument");

export const $deleteDocumentWorkflow = atom(null, async (get, set, documentId: string) => {
	logger.debug("Starting delete document workflow", { documentId });

	// Get the document to find what needs to be deleted
	const document = get($document(documentId));
	if (!document) {
		logger.error("Document not found", { documentId });
		throw new Error(`Document not found: ${documentId}`);
	}

	const local = document.local;
	const remotes = await get($gitRemotes(documentId));
	const remote = document.remote;
	const publishingRemotes =
		remotes?.filter((remoteEntry) => remoteEntry.name === GIT_PUBLISHING_REMOTE_NAME) ?? [];
	const workspaceDeleteTargets = buildWorkspaceDeleteTargets(
		Object.values(remote).map((entry) => entry.workspace),
		publishingRemotes
	);

	logger.debug("Document found, planning deletions", {
		documentId,
		hasLocal: !isEmpty(local),
		hasRemote: workspaceDeleteTargets.length > 0,
		remoteTargets: workspaceDeleteTargets.map((target) => ({
			candidates: target.candidates,
			required: target.required,
			source: target.source,
		})),
	});

	// Optimistically remove from UI immediately
	set($removeDocumentFromDocsList, documentId);

	// Perform deletions in parallel
	const deletionPromises: Promise<void>[] = [];

	// Delete from disk if local exists
	if (!isEmpty(local)) {
		const deleteFromDiskPromise = (async () => {
			try {
				const localEntry = Object.values(local)[0];
				if (localEntry) {
					logger.debug("Deleting document from disk", { path: localEntry.path });
					const fsApi = fs();
					await set(fsApi.remove, { filePath: localEntry.path });
					logger.debug("Document deleted from disk", { path: localEntry.path });
				}
			} catch (error) {
				logger.error("Error deleting document from disk", {
					documentId,
					cause: error,
				});
				throw error;
			}
		})();
		deletionPromises.push(deleteFromDiskPromise);
	}

	// Delete from remote API
	if (workspaceDeleteTargets.length > 0) {
		const deleteFromRemotePromise = (async () => {
			const docsApi = get($docsApi);
			const { getAccessTokenSilently } = get($accessTokenFunc);
			const hasRequiredRemoteDelete = workspaceDeleteTargets.some(
				(target) => target.required
			);

			if (!getAccessTokenSilently) {
				logger.warn("No access token function available for remote deletion", {
					documentId,
				});
				if (hasRequiredRemoteDelete) {
					throw new Error(
						"Failed to delete remote document: authentication function unavailable"
					);
				}
				return;
			}

			const token = await getAccessTokenSilently();
			if (!token) {
				logger.warn("No access token available for remote deletion", { documentId });
				if (hasRequiredRemoteDelete) {
					throw new Error("Failed to delete remote document: unauthenticated");
				}
				return;
			}

			logger.debug("Deleting document from remote workspaces", {
				documentId,
				targets: workspaceDeleteTargets.map((target) => target.candidates),
			});

			const failures = await deleteRemoteDocumentFromTargets(
				workspaceDeleteTargets,
				(workspaceIdentifier: string) =>
					docsApi.document.remote.delete(workspaceIdentifier, documentId, token)
			);

			failures.forEach((failure) => {
				logger.error("Failed to delete document from workspace candidates", {
					documentId,
					candidates: failure.target.candidates,
					required: failure.target.required,
					source: failure.target.source,
					causes: failure.errors,
				});
			});

			const requiredFailures = failures.filter((failure) => failure.target.required);
			if (requiredFailures.length > 0) {
				throw new Error(
					`Failed to delete remote document in required workspaces: ${requiredFailures
						.map((failure) => failure.target.candidates.join("|"))
						.join(", ")}`
				);
			}

			logger.debug("Document deleted from remote workspaces", { documentId });
		})();
		deletionPromises.push(deleteFromRemotePromise);
	}

	// Delete document-scoped environment files
	if (env.isDesktopApp) {
		const deleteDocEnvsPromise = (async () => {
			try {
				logger.debug("Deleting document environment files", { documentId });
				await set($deleteDocumentEnvironments, documentId);
				logger.debug("Document environment files deleted", { documentId });
			} catch (error) {
				logger.error("Error deleting document environment files", {
					documentId,
					cause: error,
				});
				// Don't throw - env file cleanup errors shouldn't fail the whole operation
			}
		})();
		deletionPromises.push(deleteDocEnvsPromise);
	}

	// Delete from cache (SQLite) if desktop app
	if (env.isDesktopApp) {
		const deleteFromCachePromise = (async () => {
			try {
				logger.debug("Deleting document from cache", { documentId });
				const cache = getCache();
				await cache.documents.delete(documentId);
				await cache.templateOnboarding.delete(documentId);
				logger.debug("Document deleted from cache", { documentId });
			} catch (error) {
				logger.error("Error deleting document from cache", {
					documentId,
					cause: error,
				});
				// Don't throw - cache deletion errors shouldn't fail the whole operation
			}
		})();
		deletionPromises.push(deleteFromCachePromise);
	}

	// Wait for all deletions to complete.
	// Promise.allSettled itself never throws; failures are represented in the settled results.
	const results = await Promise.allSettled(deletionPromises);
	const rejected = results.filter(
		(result): result is PromiseRejectedResult => result.status === "rejected"
	);
	if (rejected.length > 0) {
		logger.error("Delete document workflow failed", {
			documentId,
			failures: rejected.map((result) => result.reason),
		});
		logger.debug("Rolling back optimistic document deletion", { documentId });
		set($restoreDocumentInDocsList, documentId);
		throw new Error(
			`Delete document workflow failed for ${documentId}: ${String(rejected[0]?.reason)}`
		);
	}

	// Delete workflows and workflow runs targeting this document.
	// This runs after required deletions succeed to avoid irreversible data loss
	// if a required deletion fails and the document is rolled back.
	if (env.isDesktopApp) {
		try {
			logger.debug("Deleting document workflows", { documentId });
			await set($deleteDocumentWorkflows, documentId);
			logger.debug("Document workflows deleted", { documentId });
		} catch (error) {
			logger.error("Error deleting document workflows", {
				documentId,
				cause: error,
			});
			// Don't throw - workflow cleanup errors shouldn't fail the whole operation
		}
	}

	logger.debug("Delete document workflow completed successfully", { documentId });
});
$deleteDocumentWorkflow.debugLabel = "deleteDocumentWorkflow";
